/**
 * Spaces HTTP base URL.
 *
 * Default: `/api/s/_SID_/spaces` — Go (`api.go`) proxies to the Node microservice; `@pendo/http`
 * substitutes `_SID_`.
 *
 * Set **`SPACES_API_BASE`** for the dev bundle (e.g. in the shell or via
 * `npm run start --spaces-proxy=http://localhost:9003`, same idea as `--proxy` for the main
 * backend). When the base is not `/_SID_/`, this module adds `X-Subscription-Id` /
 * `X-User-Id` (etc.) for the microservice.
 *
 * Base URL is memoized after first read (not in Jest, where `SPACES_API_BASE` changes per test).
 * Header values are still resolved on every request.
 */

import { getActiveSubscription, getActiveUser } from '@/data/session/session-globals';

const PROD_BASE = '/api/s/_SID_/spaces';

function trimBase (url: string): string {
    return url.replace(/\/$/, '');
}

function computeSpacesApiBase (): string {
    const raw = process.env.SPACES_API_BASE?.trim();
    if (raw) {
        return trimBase(raw);
    }

    return PROD_BASE;
}

let resolvedSpacesEndpoint: { base: string; viaSubscriptionProxy: boolean } | null = null;

function resolveSpacesEndpoint (): { base: string; viaSubscriptionProxy: boolean } {
    if (process.env.NODE_ENV !== 'test' && resolvedSpacesEndpoint) {
        return resolvedSpacesEndpoint;
    }
    const base = computeSpacesApiBase();
    const next = { base, viaSubscriptionProxy: base.includes('/_SID_/') };
    if (process.env.NODE_ENV !== 'test') {
        resolvedSpacesEndpoint = next;
    }

    return next;
}

export function getSpacesApiBase (): string {
    return resolveSpacesEndpoint().base;
}

/**
 * Pendo tenant headers from session (for requests that cannot rely on the Go proxy injecting them).
 * `<img src>` cannot send these; use with `fetch` + blob URL for `thumbnail-image` when needed.
 */
export function getSpacesTenantHeaders (): Record<string, string> {
    const sub = getActiveSubscription();
    const user = getActiveUser();
    const headers: Record<string, string> = {};
    if (sub?.id) {
        headers['X-Subscription-Id'] = String(sub.id);
    }
    if (sub?.name) {
        headers['X-Subscription-Name'] = String(sub.name);
    }
    if (user?.id) {
        headers['X-User-Id'] = String(user.id);
    }

    return headers;
}

/** Same-origin fetch init for `GET .../thumbnail-image` (includes cookies + tenant headers). */
export function getSpacesThumbnailRequestInit (): RequestInit {
    const headers = getSpacesTenantHeaders();

    return {
        credentials: 'include',
        ...(Object.keys(headers).length ? { headers } : {}),
    };
}

/**
 * Same-origin URL for the PNG stream when the API sets `thumbnailViaSpacesApi` (no GCS signing).
 * Card UI loads this via `fetch` + blob (`space-card-thumbnail.vue`) when tenant headers are required;
 * `<img :src>` alone cannot send them.
 */
export function getSpaceThumbnailProxyUrl (spaceId: string): string {
    const sub = getActiveSubscription();
    const sid = sub?.id != null ? String(sub.id) : '_SID_';
    const base = getSpacesApiBase().replace('_SID_', sid);

    return `${base}/${encodeURIComponent(spaceId)}/thumbnail-image`;
}

/** Same-origin URL for item images (mirrors {@link getSpaceThumbnailProxyUrl}). */
export function getSpaceItemImageProxyUrl (spaceId: string, itemId: string): string {
    const sub = getActiveSubscription();
    const sid = sub?.id != null ? String(sub.id) : '_SID_';
    const base = getSpacesApiBase().replace('_SID_', sid);

    return `${base}/${encodeURIComponent(spaceId)}/items/${encodeURIComponent(itemId)}/image`;
}

/** Extra headers only when not using the app `/_SID_/` proxy. */
export function getSpacesHttpConfig (): { headers?: Record<string, string> } {
    if (resolveSpacesEndpoint().viaSubscriptionProxy) {
        return {};
    }

    const headers = getSpacesTenantHeaders();

    return Object.keys(headers).length ? { headers } : {};
}
